import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../seo/SeoHead";
import PageBreadcrumb from "../seo/PageBreadcrumb";
import ServicePageShell from "../ui/ServicePageShell";
import { ServiceTag } from "../ui/ServiceTag";
import CallButton from "../ui/CallButton";
import DirectionsButton from "../ui/DirectionsButton";
import BusinessHours from "../ui/BusinessHours";
import ReviewSection from "../ui/ReviewSection";
import TrustBadges from "../ui/TrustBadges";
import VehicleBrandGrid from "../ui/VehicleBrandGrid";
import WarrantyNotice from "../ui/WarrantyNotice";
import ServiceAreas from "../ui/ServiceAreas";
import { PHONE_DISPLAY } from "../../lib/businessInfo";
import heroImg from "../../assets/Jaguar.png";

const bulletClass =
  "flex items-start gap-2 text-sm text-slate-700 sm:text-base leading-relaxed";

const linkClass =
  "text-teal-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40 focus-visible:ring-offset-2 rounded-sm";

const JaguarRepair = () => {
  return (
    <>
      <SeoHead
        title="Jaguar Repair in Temecula | Advanced Automotive Smog"
        description="Independent Jaguar repair, diagnostics, and maintenance in Temecula. Schedule service for your Jaguar with a shop experienced in European vehicle care."
        path="/jaguar-repair"
      />

      <ServicePageShell>
        <PageBreadcrumb
          label="Jaguar Repair"
          path="/jaguar-repair"
          description="Independent Jaguar repair, diagnostics, and maintenance in Temecula. Schedule service for your Jaguar with a shop experienced in European vehicle care."
          serviceType="Jaguar repair"
        />
        <div className="flex min-w-0 flex-col gap-8 md:flex-row md:items-start md:gap-14">
          <div className="order-1 flex w-full min-w-0 justify-center md:order-none md:w-2/5">
            <div className="relative w-full max-w-xs sm:max-w-sm">
              <div className="absolute -inset-3 rounded-3xl bg-teal-600/10 blur-xl" />
              <img
                src={heroImg}
                alt="Jaguar service and repair"
                className="relative aspect-[4/3] h-auto w-full max-h-[14rem] rounded-3xl border border-slate-200 object-cover object-center shadow-xl sm:aspect-auto sm:max-h-none"
              />
            </div>
          </div>

          <div className="w-full min-w-0 md:w-3/5">
            <ServiceTag>Jaguar · Import service</ServiceTag>
            <h1 className="mb-3 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              Jaguar Repair & Service in Temecula
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-slate-700 sm:text-base">
              We service a range of Jaguar vehicles, pairing careful
              diagnostics with the attention to detail these cars expect—from
              routine maintenance to more involved repair work.
            </p>
            <CallButton className="px-8 py-3 text-base" />
          </div>
        </div>

        <TrustBadges tone="light" className="mt-12" />
        <VehicleBrandGrid tone="light" className="mt-10" linked />
        <WarrantyNotice tone="light" className="mt-8" />

        <section className="mt-14 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Diagnostic Capability
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            Jaguar vehicles rely on integrated electronics for engine
            management, suspension, and climate systems. We use professional
            diagnostic equipment to read fault codes, test components, and
            confirm the actual cause of a concern before recommending repair.
          </p>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Signs Your Jaguar May Need Professional Diagnosis
          </h2>
          <p className="mb-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            These symptoms may have several possible causes and should be
            professionally diagnosed rather than guessed at:
          </p>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Warning lights on the instrument cluster</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Ride height, suspension, or handling changes</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Unusual noises, vibration, or overheating</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Electrical faults or inconsistent accessory behavior</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Jaguar Service Categories
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Check-engine light and electrical diagnostics</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Brake and suspension service</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Cooling-system repair</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Transmission service</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Air conditioning service</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Factory-scheduled maintenance</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Smog and emissions support when required</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Scheduled Maintenance
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            Staying current on fluid changes, inspections, and
            factory-recommended service intervals helps prevent larger
            repairs down the road. We follow manufacturer-recommended
            maintenance schedules so you know what your Jaguar needs and
            when.
          </p>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Why Jaguar Owners Choose an Independent Shop
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            Jaguar owners often come to us after comparing dealership wait
            times and pricing with a local independent option. We apply the
            same careful diagnostic approach with more direct
            communication—explaining what we find and what it will take to
            fix it.
          </p>
        </section>

        <BusinessHours tone="light" className="mt-12" />
        <ReviewSection tone="light" className="mt-12" />

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Related Services
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <Link to="/contact" className={linkClass}>
                Contact &amp; appointments
              </Link>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <Link to="/european-auto-repair" className={linkClass}>
                European Auto Repair
              </Link>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <Link to="/auto-repair" className={linkClass}>
                Auto Repair
              </Link>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <Link to="/check-engine-light" className={linkClass}>
                Check Engine Light Diagnostics
              </Link>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <Link to="/transmission-repair" className={linkClass}>
                Transmission Repair
              </Link>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <Link to="/services/smog-emissions-services" className={linkClass}>
                Smog &amp; Emissions Testing
              </Link>
            </li>
          </ul>
        </section>

        <ServiceAreas tone="light" className="mt-12 border-t border-slate-200 pt-12" />

        <section className="mt-12 rounded-2xl border border-slate-200 bg-teal-50/80 p-6 text-center shadow-sm ring-1 ring-teal-600/10 sm:p-10">
          <h2 className="text-lg font-semibold text-navy-900 sm:text-xl">
            Book Jaguar service or diagnostics
          </h2>
          <p className="mt-2 text-sm text-slate-700 sm:text-base">
            Call {PHONE_DISPLAY} or tap directions to visit our shop on Rio Nedo.
          </p>
          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <CallButton className="px-8 py-3 text-base" />
            <DirectionsButton className="px-8 py-3 text-base" />
          </div>
        </section>
      </ServicePageShell>
    </>
  );
};

export default JaguarRepair;
